import * as React from 'react';
import {wrapDisplayName} from 'react-recompose';
import pickBy from 'lodash/pickBy';

import {ENTER, SPACE} from '@packages/utilities/key-utils';
import {weakMemo, memoize} from '@packages/utilities/memo';
import {emptyObject} from '@packages/utilities/fp/utils';
import {unselectable} from '../constants/Styles';
import {mergeRefs} from '../utils/mergeRefs';

/**
 * Removes undefined values. Without this filter, defined short-hands such as
 * `borderColor` will be overruled by `undefined` long-hand values such as
 * `borderColorTop` or `borderColorBottom`.
 *
 * @see https://webflow.atlassian.net/browse/PDTY-2398
 * @param {Object} styles
 * @returns {Object} styles
 */
const filterUndefinedValues = (styles) =>
  pickBy(styles, (value) => typeof value !== 'undefined');

const mergeStyle = memoize((cursor) =>
  weakMemo(
    ({
      borderBottomColor,
      borderColor, // Remaps to top, right, bottom, left.
      borderLeftColor,
      borderRightColor,
      borderTopColor,
      ...style
    }) => {
      const enhancedStyles = {
        /**
         * To help quiet the warnings from React when using the shorthand
         * borderColor for our styled library.
         *
         * "Updating a style property during rerender (borderColor) when a
         * conflicting property is set (border) can lead to styling bugs."
         *
         * @see https://github.com/facebook/react/blob/cae635054e17a6f107a39d328649137b83f25972/packages/react-dom/src/client/CSSPropertyOperations.js#L124
         * @see https://webflow.atlassian.net/browse/PDTY-2457
         */
        borderBottomColor: borderColor || borderBottomColor,
        borderLeftColor: borderColor || borderLeftColor,
        borderRightColor: borderColor || borderRightColor,
        borderTopColor: borderColor || borderTopColor,
        outline: 0,
        ...unselectable,
        cursor, // should go after unselectable to prevent overwriting cursor
        ...style,
      };

      return filterUndefinedValues(enhancedStyles);
    }
  )
);

// Identity-stable ref merger per Clickable instance. Caches the last
// `(forwardedRef, _ref)` pair and reuses the merged callback when both
// inputs are unchanged, so React doesn't detach/reattach the ref on every
// render of the wrapping forwardRef shim.
const makeRefMerger = () => {
  let lastForwardedRef = null;
  let lastLegacyRef = null;
  let lastMergedRef = null;
  return (forwardedRef, legacyRef) => {
    if (!forwardedRef) return legacyRef ?? null;
    if (!legacyRef) return forwardedRef;
    if (
      forwardedRef === lastForwardedRef &&
      legacyRef === lastLegacyRef &&
      lastMergedRef
    ) {
      return lastMergedRef;
    }
    lastForwardedRef = forwardedRef;
    lastLegacyRef = legacyRef;
    lastMergedRef = mergeRefs([forwardedRef, legacyRef]);
    return lastMergedRef;
  };
};

const clickable = (Component) => {
  class Clickable extends React.PureComponent {
    static displayName = wrapDisplayName(Component, 'clickable');

    onKeyDown = (event) => {
      const {disabled, onClick, onKeyDown} = this.props;
      const keyCode = event.which;

      onKeyDown && onKeyDown(event);

      // If the underlying component is a real button, using focus+keypress of
      // space or enter will trigger the button's onclick handler natively, thus
      // we can return early. Otherwise, we would inadvertently fire `onClick`
      // twice in this component.
      if (Component === 'button') return;

      if (
        onClick &&
        !disabled &&
        !event.defaultPrevented &&
        (keyCode === ENTER || keyCode === SPACE)
      ) {
        onClick(event);
      }
    };

    onClick = (event) => {
      const {disabled, onClick} = this.props;
      event.stopPropagation();

      if (!disabled && onClick) {
        onClick(event);
      }
    };

    render() {
      const {
        disabled,
        style = emptyObject,
        'data-automation-id': dataAutomationId,
        keepOutline = false,
        keyboardFocus,
        _ref,
      } = this.props;
      let {cursor = 'default'} = this.props;

      if (disabled) {
        cursor = cursor === 'not-allowed' ? cursor : 'default';
      }

      const props = {
        ...this.props,
        'data-focus': keyboardFocus,
        tabIndex: disabled
          ? undefined
          : this.props.hasOwnProperty('tabIndex')
            ? this.props.tabIndex
            : 0,
        style: mergeStyle(cursor)(style),
        onClick: this.onClick,
        onKeyDown: this.onKeyDown,
      };

      if (keepOutline) {
        if (props.style.outline === 0) {
          // eslint-disable-next-line webflow/no-delete
          delete props.style.outline;
        }
      }

      if (Component === 'button') {
        // React will not call onMouseLeave events on a disabled button, however it _will_ call onMouseEnter.
        // Since they are not called 1:1, it makes it so a button can have out-of-sync states for hover, active, etc states.
        // To "fix" this until React does, we're going to remove the actual `disabled` DOM attribute and then use `aria-disabled`
        // so we still have the a11y perks from it.
        // src: https://github.com/facebook/react/blob/4c6470cb3b821f3664955290cd4c4c7ac0de733a/packages/react-dom/src/events/getListener.js#L24
        if (props.disabled) {
          // eslint-disable-next-line webflow/no-delete
          delete props.disabled;
          props['aria-disabled'] = true;
        }
      }

      return (
        <Component
          data-automation-id={dataAutomationId}
          ref={_ref}
          {...props}
        />
      );
    }
  }

  // forwardRef shim around the legacy class so consumers can use React's
  // standard `ref` API (e.g. `tooltipTrigger` via `cloneElement({ref})`)
  // without any change at the call site. Internally we translate React
  // `ref` to the class's existing `_ref` prop, which the class already
  // attaches to the rendered Component (see `ref={_ref}` in render above).
  //
  // The legacy `_ref={someRef}` prop is preserved unchanged for the ~70
  // call sites that already use it. When both `ref` and `_ref` are
  // provided, they are merged so both receive the DOM node.
  //
  // The class itself is intentionally untouched so PureComponent
  // semantics, keyboard handling, focus styles, and `disabled` button
  // behavior are guaranteed identical to before.
  const ClickableWithRef = React.forwardRef((props, ref) => {
    // Per-instance merger; identity-stable across re-renders so React
    // doesn't detach/reattach the legacy `_ref` between renders.
    const merger = React.useRef(null);
    if (merger.current === null) merger.current = makeRefMerger();

    const {_ref: legacyRef, ...rest} = props;
    const finalRef = merger.current(ref, legacyRef);
    return <Clickable {...rest} _ref={finalRef} />;
  });
  ClickableWithRef.displayName = wrapDisplayName(Component, 'clickable');

  // Cast back to the original Clickable class type so JSX type inference
  // at consumer sites is unchanged. Without this cast, TypeScript types
  // the forwardRef result as `ForwardRefExoticComponent<RefAttributes<any>>`
  // (no `Props` generic supplied), erasing all of the loose prop typing
  // that the original class had — `styled(Clickable)`, `<Item style={...}>`,
  // `<Item cursor="..." />`, etc. all start failing TS2339/TS2322.
  // Same shape as the cast in the `themeable` HOC fix elsewhere in this PR.
  return /** @type {typeof Clickable} */ (
    /** @type {unknown} */ (ClickableWithRef)
  );
};

export default clickable;

export const Clickable = clickable('div');
